import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  DollarSign,
  RefreshCw,
  Loader2,
  ArrowRightLeft,
  TrendingUp,
  Clock,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { DashboardLayout } from '../components/DashboardLayout';
import api from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

const currencyFlags = {
  GBP: '\uD83C\uDDEC\uD83C\uDDE7',
  EUR: '\uD83C\uDDEA\uD83C\uDDFA',
  USD: '\uD83C\uDDFA\uD83C\uDDF8',
  DKK: '\uD83C\uDDE9\uD83C\uDDF0',
};

const currencyNames = {
  GBP: 'British Pound',
  EUR: 'Euro',
  USD: 'US Dollar',
  DKK: 'Danish Krone',
};

const currencies = ['GBP', 'EUR', 'USD', 'DKK'];

export function FxRatesPage() {
  const [rates, setRates] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Converter state
  const [amount, setAmount] = useState('1000');
  const [fromCurrency, setFromCurrency] = useState('GBP');
  const [toCurrency, setToCurrency] = useState('EUR');
  const [convertedResult, setConvertedResult] = useState(null);
  const [isConverting, setIsConverting] = useState(false);

  const fetchRates = async () => {
    try {
      const res = await api.get('/fx/rates');
      setRates(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRates();
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await api.post('/fx/refresh');
      toast.success('FX rates refreshed');
      await fetchRates();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleConvert = async () => {
    if (!amount || Number(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setIsConverting(true);
    try {
      const res = await api.get('/fx/convert', {
        params: {
          amount: Number(amount),
          from: fromCurrency,
          to: toCurrency,
        },
      });
      setConvertedResult(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsConverting(false);
    }
  };

  const handleSwapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    setConvertedResult(null);
  };

  // Extract rate pairs from response
  const rateList = rates?.rates || rates?.data || rates || {};
  const lastUpdated = rates?.last_updated || rates?.updated_at || null;

  // Build rate table from response data
  const rateEntries = [];
  if (typeof rateList === 'object' && !Array.isArray(rateList)) {
    for (const [pair, rate] of Object.entries(rateList)) {
      if (typeof rate === 'number') {
        rateEntries.push({ pair, rate });
      }
    }
  }
  if (Array.isArray(rateList)) {
    for (const item of rateList) {
      rateEntries.push({
        pair: item.pair || `${item.from}/${item.to}`,
        rate: item.rate || item.value,
      });
    }
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="fx-rates-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Exchange Rates</h1>
            <p className="text-muted-foreground mt-1">Live FX rates and currency converter</p>
          </div>
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>Updated: {new Date(lastUpdated).toLocaleString('en-GB')}</span>
              </div>
            )}
            <Button onClick={handleRefresh} disabled={isRefreshing} variant="outline">
              {isRefreshing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {isRefreshing ? 'Refreshing...' : 'Refresh Rates'}
            </Button>
          </div>
        </div>

        {/* Current Rates Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {currencies.map((currency, index) => (
            <motion.div
              key={currency}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="hover:shadow-md transition-all">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{currencyFlags[currency]}</span>
                    <div>
                      <p className="font-bold text-lg">{currency}</p>
                      <p className="text-xs text-muted-foreground">{currencyNames[currency]}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Rates Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Current Rates
            </CardTitle>
            <CardDescription>Exchange rate pairs</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : rateEntries.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No rate data available</p>
                <p className="text-sm text-muted-foreground mt-1">Click refresh to fetch the latest rates</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Currency Pair</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Inverse</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rateEntries.map((entry, index) => (
                    <motion.tr
                      key={entry.pair || index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.03 }}
                      className="border-b transition-colors hover:bg-muted/50"
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{entry.pair}</Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-lg">
                        {Number(entry.rate).toFixed(4)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">
                        {entry.rate > 0 ? (1 / entry.rate).toFixed(4) : '--'}
                      </TableCell>
                    </motion.tr>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Currency Converter */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5" />
              Currency Converter
            </CardTitle>
            <CardDescription>Convert between supported currencies</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="space-y-1.5 flex-1">
                <Label htmlFor="convert-amount">Amount</Label>
                <Input
                  id="convert-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setConvertedResult(null);
                  }}
                  placeholder="Enter amount"
                />
              </div>
              <div className="space-y-1.5 w-full sm:w-[140px]">
                <Label>From</Label>
                <Select
                  value={fromCurrency}
                  onValueChange={(val) => {
                    setFromCurrency(val);
                    setConvertedResult(null);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((c) => (
                      <SelectItem key={c} value={c}>
                        {currencyFlags[c]} {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSwapCurrencies}
                className="shrink-0"
                title="Swap currencies"
              >
                <ArrowRightLeft className="h-4 w-4" />
              </Button>
              <div className="space-y-1.5 w-full sm:w-[140px]">
                <Label>To</Label>
                <Select
                  value={toCurrency}
                  onValueChange={(val) => {
                    setToCurrency(val);
                    setConvertedResult(null);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((c) => (
                      <SelectItem key={c} value={c}>
                        {currencyFlags[c]} {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleConvert} disabled={isConverting}>
                {isConverting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <DollarSign className="h-4 w-4 mr-2" />
                )}
                Convert
              </Button>
            </div>

            {/* Conversion Result */}
            {convertedResult && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 p-6 bg-muted/50 rounded-lg border"
              >
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
                  <div>
                    <p className="text-sm text-muted-foreground">From</p>
                    <p className="text-2xl font-bold">
                      {currencyFlags[fromCurrency]} {Number(amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })} {fromCurrency}
                    </p>
                  </div>
                  <ArrowRightLeft className="h-6 w-6 text-muted-foreground shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground">To</p>
                    <p className="text-2xl font-bold text-primary">
                      {currencyFlags[toCurrency]}{' '}
                      {Number(convertedResult.converted_amount ?? convertedResult.result ?? 0).toLocaleString('en-GB', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      {toCurrency}
                    </p>
                  </div>
                </div>
                {(convertedResult.rate || convertedResult.exchange_rate) && (
                  <p className="text-center text-sm text-muted-foreground mt-3">
                    Rate: 1 {fromCurrency} = {Number(convertedResult.rate || convertedResult.exchange_rate).toFixed(4)} {toCurrency}
                  </p>
                )}
              </motion.div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
